/**
 * Renders a graph using Chart.js based on the Image Property Styling.
 * 
 * @param {HTMLCanvasElement} canvasElement The canvas element to render the chart in.
 * @param {Array<{x: string, y: number}>} dataPoints An array of data points to render.
 * 
 * @returns {void}
 */
function propTrackChart(canvasElement, dataPoints) {
  // 1. Determine if this chart is for rentals by checking the canvas ID
  const isRental = canvasElement.id.includes("rental");
  const canvasId = canvasElement.id;
  // 2. Pick rounding base (nearest $100 for rentals, nearest $100,000 otherwise)
  const roundingBase = isRental ? 100 : 100000;

  // 1. Extract just the y-values for min/max calculations
  const dataValues = dataPoints.map(dp => dp.y);

  // 2. Extract just the x-values for labels
  const Labels = dataPoints.map(dp => dp.x);

  const realMin = Math.min(...dataValues);
  const realMax = Math.max(...dataValues);

  // Round down (floor) the min to the nearest 100 or 100,000
  const roundedMin = Math.floor(realMin / roundingBase) * roundingBase;

  // Round up (ceil) the max to the nearest 100 or 100,000
  const roundedMax =
    Math.ceil(realMax / roundingBase * roundingBase) + roundingBase;

  // We want 4 labels, i.e. 3 intervals from min to max
  const range = roundedMax - roundedMin;
  const stepSize = range / 3;

  const myChart = new Chart(canvasElement, {
    type: "line",
    data: {
      datasets: [
        {
          label: "Median Price",
          data: dataPoints,
          borderWidth: 2,
          fill: true,
          backgroundColor: "rgba(247, 148, 29, 0.2)",
          borderColor: "#f15a22",
          tension: 0.2,
          pointRadius: 0, // Hide points by default
          pointHitRadius: 10,
          pointHoverRadius: 20, // Show a bigger circle on hover
          pointHoverBackgroundColor: "rgba(247, 148, 29, 0.2)",
          pointHoverBorderColor: "#454545",
          pointHoverBorderWidth: 2
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          borderColor: "#f1f1f1",
          borderWidth: 1,
          backgroundColor: "#ffffff",
          displayColors: false, // no color box
          titleAlign: "center",
          bodyAlign: "center",
          footerAlign: "center",
          // 1. DATE (title)
          titleColor: "#666666",
          titleFont: {
            size: 12,
            weight: "normal"
          },
          // 2. PRICE (body)
          bodyColor: "#454545", // black
          bodyFont: {
            size: 16, // larger text
            weight: "bold" // bold
          },
          // 3. TEXT UNDERNEATH (footer)
          footerColor: "#666666",
          footerFont: {
            size: 13,
            weight: "normal"
          },
          callbacks: {
            title: tooltipItems => {
              const rawDate = tooltipItems[0].parsed.x;
              return new Date(rawDate).toLocaleDateString("en-US", {
                month: "long",
                year: "numeric"
              });
            },
            // Tooltip label callback
            label: tooltipItem => {
              const rawValue = tooltipItem.parsed.y;
              // Format the number with thousands separators, but NOT currency
              const formattedNumber = new Intl.NumberFormat("en-US", {
                style: "decimal", // no "currency"
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(rawValue);
              // Prepend "$" to that string
              return "$" + formattedNumber;
            },
            footer: () => {
              return "Average Sale Price Based on sales for this period.";
            }
          }
        }
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: "month",
            displayFormats: {
              month: "MMM 'yy" // e.g., "Jan '20"
            }
          },
          ticks: {
            // Hide all months except January in tick labels
            callback: function (value, index, ticks) {
              // 'value' is a timestamp
              const date = new Date(value);
              const formatted = format(date, "MMM ''yy");
              // if (formatted.startsWith("Jan")) {
              //   return formatted;
              // }
              // return "";
              return formatted;
            }
          }
        },
        y: {
          // Forced range to nearest 100k below/above
          min: roundedMin,
          max: roundedMax,
          ticks: {
            stepSize: stepSize,
            callback: function (value) {
              if (isRental) {
                return "$" + value.toFixed(0);
              }
              if (value >= 1000000) {
                return "$" + (value / 1000000).toFixed(1) + "mil";
              } else {
                return "$" + (value / 1000).toFixed(0) + "k";
              }
            }
          }
        }
      }
    }
  });

  return myChart;
}

/**
 * 
 */
function updateDisplayedSection(charts) {
  // Get user’s selection
  const transaction = document.querySelector(
    'input[name="guideTransactionType"]:checked'
  ).value;

  const bedrooms = document.querySelector(
    'input[name="guide-bedrooms"]:checked'
  ).value;

  // Hide non-matching, show matching
  const combos = document.querySelectorAll(".combo-content");
  combos.forEach(combo => {
    const matches =
      combo.dataset.transaction === transaction &&
      combo.dataset.bedrooms === bedrooms;
    combo.style.display = matches ? "block" : "none";
    showContainer(combo.id, charts);
  });
}

function initPropTrackSelections(bedroomRadios, bedroomDropdownButton, charts) {
  // Update the displayed label on the dropdown when radio changed.
  bedroomRadios.forEach(radio => {
    radio.addEventListener("change", () => {
      const selectedText = radio.closest("label").innerText.trim();
      bedroomDropdownButton.textContent = selectedText;
      updateDisplayedSection(charts);
    });
  });

  // 2. Listen for changes on the Buy / Rent radio buttons
  const transactionRadios = document.querySelectorAll(
    'input[name="guideTransactionType"]'
  );
  transactionRadios.forEach(radio => {
    radio.addEventListener("change", () => {
      updateDisplayedSection(charts);
    });
  });

  // 4. On page load, ensure the correct default is displayed
  updateDisplayedSection(charts);
}

function showContainer(containerId, charts) {
  const container = document.getElementById(containerId);
  // Give the browser a moment to apply layout
  setTimeout(() => {
    // Find the canvas inside
    if (container.style.display == "block") {
      const canvas = container.querySelector("canvas");
      if (canvas) {
        // Look up the chart instance we stored
        const chart = charts[canvas.id];
        if (chart) {
          chart.resize(); // or chart.resize();
        }
      }
    }
  }, 10);
}

/**
 * Helper function to create charts from a given data object.
 * @param {Object} dataObject  The bedroom data (either sale or rental)
 * @param {string} prefix      For example, 'sale' or 'rental', used to build element IDs
 */
function createChartsForData(dataObject, prefix, charts) {
  Object.keys(dataObject).forEach(bedroom => {
    const data = dataObject[bedroom];

    // Build dataPoints for the chart
    const dataPoints = data.labels.map((label, index) => ({
      x: label,
      y: data.values[index]
    }));

    // Find the corresponding <canvas> using the prefix
    const canvasId = `${prefix}-${bedroom}-house`;
    const canvas = document.getElementById(canvasId);

    if (canvas) {
      const chartInstance = understrap.propTrackChart(canvas, dataPoints);
      charts[canvasId] = chartInstance;
    }
  });
}

export {
  propTrackChart,
  initPropTrackSelections,
  createChartsForData,
  showContainer
};
